import { supabase } from "@/lib/supabase-client";
import { LocationType } from "@/types/db/locations";

export type LocationBannedUserType = {
  id: string; // This is the location id
  tenant_id: string;
  user_id: string;
  reason?: string | null;
  created_at?: string;
};

export const locationService = {
  async getLocations(): Promise<(LocationType & { banned_users?: LocationBannedUserType[] })[]> {
    const { data, error } = await supabase
      .schema("lists")
      .from("locations")
      .select("*")
      .order("name", { ascending: true });

    if (error) {
      throw Error(error.message);
    }

    // Load banned users from link schema for each location
    const locationsWithBannedUsers = await Promise.all(
      (data || []).map(async (location) => {
        const { data: bannedUsers } = await supabase
          .schema("link")
          .from("locations_banned_users")
          .select("*")
          .eq("id", location.id);

        return {
          ...location,
          banned_users: bannedUsers || [],
        };
      }),
    );

    return locationsWithBannedUsers;
  },

  async getLocationById(id: string): Promise<LocationType & { banned_users?: LocationBannedUserType[] }> {
    const { data, error } = await supabase.schema("lists").from("locations").select("*").eq("id", id).single();
    if (error) {
      throw Error(error.message);
    }

    // Load banned users from link schema
    const { data: bannedUsers } = await supabase.schema("link").from("locations_banned_users").select("*").eq("id", id);

    return {
      ...data,
      banned_users: bannedUsers || [],
    } as LocationType & { banned_users?: LocationBannedUserType[] };
  },

  async addLocation(locationData: {
    name: string;
    description?: string | null;
    street: string;
    city: string;
    state: string;
    zip: string;
    instructions?: string | null;
    market_id?: string | null;
    tenant_id: string;
  }): Promise<LocationType> {
    const { data, error } = await supabase
      .schema("lists")
      .from("locations")
      .insert([{ ...locationData, meta_data: {} }])
      .select()
      .single();
    if (error) {
      console.error("Error adding Location:", error.message);
      throw new Error(error.message);
    }
    return data as LocationType;
  },

  async saveRowPatches(patches: Partial<LocationType>[]): Promise<LocationType[]> {
    const settled = await Promise.all(
      patches
        .filter((patch) => !!patch.id)
        .map(async (patch) => {
          const { data, error } = await supabase
            .schema("lists")
            .from("locations")
            .update(patch)
            .eq("id", patch.id!)
            .select()
            .single();

          if (error) throw error;
          return data as LocationType;
        }),
    );

    return settled;
  },

  async deleteLocationsByIds(locationIds: string[]): Promise<boolean> {
    const { error } = await supabase.schema("lists").from("locations").delete().in("id", locationIds);
    if (error) {
      console.error("Error deleting Locations by IDs:", error.message);
      return false;
    }
    return true;
  },

  // Manage locations_banned_users link table
  async addBannedUsersToLocation(
    locationId: string,
    tenantId: string,
    userIds: string[],
    reasons?: { [userId: string]: string },
  ): Promise<boolean> {
    const records = userIds.map((userId) => ({
      id: locationId, // id column represents the location
      user_id: userId,
      tenant_id: tenantId,
      reason: reasons?.[userId] || null,
    }));

    const { error } = await supabase.schema("link").from("locations_banned_users").insert(records);
    if (error) {
      console.error("Error adding banned users to location:", error.message);
      throw new Error(error.message);
    }
    return true;
  },

  async removeBannedUsersFromLocation(locationId: string, userIds: string[]): Promise<boolean> {
    const { error } = await supabase
      .schema("link")
      .from("locations_banned_users")
      .delete()
      .eq("id", locationId)
      .in("user_id", userIds);

    if (error) {
      console.error("Error removing banned users from location:", error.message);
      throw new Error(error.message);
    }
    return true;
  },

  async updateBannedUserReason(locationId: string, userId: string, reason: string | null): Promise<boolean> {
    const { error } = await supabase
      .schema("link")
      .from("locations_banned_users")
      .update({ reason })
      .eq("id", locationId)
      .eq("user_id", userId);

    if (error) {
      console.error("Error updating banned user reason:", error.message);
      throw new Error(error.message);
    }
    return true;
  },

  async syncLocationBannedUsers(
    locationId: string,
    tenantId: string,
    newUserIds: string[],
    reasons?: { [userId: string]: string },
  ): Promise<boolean> {
    // Get current banned users
    const { data: currentBannedUsers } = await supabase
      .schema("link")
      .from("locations_banned_users")
      .select("user_id")
      .eq("id", locationId);

    const currentUserIds = (currentBannedUsers || []).map((u) => u.user_id);

    // Find users to add and remove
    const toAdd = newUserIds.filter((id) => !currentUserIds.includes(id));
    const toRemove = currentUserIds.filter((id) => !newUserIds.includes(id));

    // Remove users
    if (toRemove.length > 0) {
      await this.removeBannedUsersFromLocation(locationId, toRemove);
    }

    // Add users
    if (toAdd.length > 0) {
      await this.addBannedUsersToLocation(locationId, tenantId, toAdd, reasons);
    }

    // Update reasons for existing users
    const existingUserIds = newUserIds.filter((id) => currentUserIds.includes(id));
    for (const userId of existingUserIds) {
      if (reasons && reasons[userId] !== undefined) {
        await this.updateBannedUserReason(locationId, userId, reasons[userId]);
      }
    }

    return true;
  },

  async getBannedUserIds(locationId: string): Promise<string[]> {
    const { data, error } = await supabase
      .schema("link")
      .from("locations_banned_users")
      .select("user_id")
      .eq("id", locationId);

    if (error) {
      throw new Error(error.message);
    }

    return data?.map((d) => d.user_id) || [];
  },
};
